import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { toast } from "sonner";

export default function ResendVerification() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      setSent(true);
      toast.success("If this email exists and is not verified, a verification email has been sent.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send verification email");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-[#1a1a1a] dark:to-[#252525] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-block bg-white dark:bg-[#2a2a2a] rounded-xl p-6 shadow-[0_35px_96px_0px_rgba(0,0,0,0.55)]">
              <img 
                src="/niello-logo.png" 
                alt="Think Niello" 
                className="h-16 mx-auto"
              />
            </div>
          </div>
          <Card className="shadow-[0_35px_96px_0px_rgba(0,0,0,0.55)] border-0">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <CardTitle className="text-2xl text-slate-800 dark:text-white">Check Your Email</CardTitle>
              <CardDescription className="text-slate-600 dark:text-gray-300">
                If an unverified account exists with that email, we've sent a new verification link.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>Didn't receive the email?</strong>
                  <br />
                  • Check your spam/junk folder
                  <br />
                  • Make sure you entered the correct email
                  <br />
                  • Wait a few minutes and try again
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  onClick={() => setSent(false)}
                  className="w-full"
                  data-testid="try-again-button"
                >
                  Try Another Email
                </Button>
                <Link to="/login" className="w-full">
                  <Button variant="ghost" className="w-full" data-testid="back-to-login-button">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Login
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-[#1a1a1a] dark:to-[#252525] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-block bg-white dark:bg-[#2a2a2a] rounded-xl p-6 shadow-[0_35px_96px_0px_rgba(0,0,0,0.55)]">
            <img 
              src="/niello-logo.png" 
              alt="Think Niello" 
              className="h-16 mx-auto"
            />
          </div>
        </div>
        <Card className="shadow-[0_35px_96px_0px_rgba(0,0,0,0.55)] border-0">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl text-slate-800 dark:text-white">Resend Verification Email</CardTitle>
            <CardDescription className="text-slate-600 dark:text-gray-300">
              Enter your email address and we'll send you a new verification link.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="resend-verification-email-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading}
                data-testid="resend-verification-submit-button"
              >
                {loading ? "Sending..." : "Send Verification Email"}
              </Button>
              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                  data-testid="back-to-login-link"
                >
                  <ArrowLeft className="w-4 h-4 inline mr-1" />
                  Back to Login
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
